import { useEffect, useRef, useState } from 'react';
import { clientApi } from '@/config/axios';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { usePermissions } from '@/features/permissions/hooks/use-permissions';
import type { ClinicalEpisode } from './types';

interface Proposal {
  event_type: string;
  payload: Record<string, unknown>;
  evidence_span: string;
}
interface NoteSection {
  heading?: string;
  event_id: string;
  event_type: string;
  subject_id: string;
  occurred_at: string;
  text: string;
  numeric_verification: boolean;
}
export interface VoiceBackfillProps {
  organizationId: string;
  episode: ClinicalEpisode;
  encounterId: string;
  allowedSubjectIds?: string[];
  onSaved?: () => void;
  offline?: boolean;
  templates?: { id: string; title: string }[];
}
function messageOf(error: unknown) {
  const data = (error as { response?: { data?: { detail?: unknown; message?: unknown } } }).response?.data;
  return typeof data?.message === 'string'
    ? data.message
    : typeof data?.detail === 'string'
      ? data.detail
      : 'Request failed. Your text and recording are retained; retry when connected.';
}
export function VoiceBackfill(props: VoiceBackfillProps) {
  // Remount on chart/encounter changes so pending requests and recorder state never leak across patients.
  // Connectivity changes must not destroy in-memory source text/audio or an uncertain retry.
  return <OnlineVoice key={`${props.organizationId}/${props.episode.id}/${props.encounterId}`} {...props} />;
}
function OnlineVoice({
  organizationId,
  episode,
  encounterId,
  allowedSubjectIds,
  onSaved,
  templates = [],
  offline = false,
}: VoiceBackfillProps) {
  const { permissions } = usePermissions();
  const [text, setText] = useState('');
  const [sourceHash, setSourceHash] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  const [time, setTime] = useState(() => new Date().toISOString());
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [selected, setSelected] = useState<number[]>([]);
  const [reviewed, setReviewed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [recording, setRecording] = useState(false);
  const [audio, setAudio] = useState<Blob | null>(null);
  const [notes, setNotes] = useState<NoteSection[] | null>(null);
  const [templateId, setTemplateId] = useState('');
  const [uncertain, setUncertain] = useState(false);
  const retry = useRef<{ request_id: string; [key: string]: unknown } | null>(null);
  const alive = useRef(true);
  const recorder = useRef<MediaRecorder | null>(null);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      const r = recorder.current;
      if (r?.state === 'recording') r.stop();
      r?.stream.getTracks().forEach((t) => t.stop());
    };
  }, []);
  const base = `/api/v1/organizations/${organizationId}/flowsheets/episodes/${episode.id}`;
  const canCreate =
    permissions.includes('create_clinical_event') || permissions.includes('create_supervised_clinical_event');
  const locked = busy || uncertain || recording;
  const networkDisabled = locked || offline;
  const hasUnsavedEvidence = !!text || recording || !!audio || uncertain || !!proposals.length;
  useEffect(() => {
    if (!hasUnsavedEvidence) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasUnsavedEvidence]);
  const reset = () => {
    setProposals([]);
    setSelected([]);
    setReviewed(false);
    retry.current = null;
  };
  const run = async (fn: () => Promise<void>) => {
    if (offline) {
      setMessage('Connection unavailable. Your text and recording remain in memory; reconnect to retry.');
      return;
    }
    setBusy(true);
    setMessage('');
    try {
      await fn();
    } catch (e) {
      if (alive.current) setMessage(messageOf(e));
    } finally {
      if (alive.current) setBusy(false);
    }
  };
  async function record() {
    if (recording) {
      recorder.current?.stop();
      return;
    }
    if (offline) return;
    setBusy(true);
    let acquired: MediaStream | null = null;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!alive.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      acquired = stream;
      const r = new MediaRecorder(stream);
      const chunks: BlobPart[] = [];
      let bytes = 0;
      r.ondataavailable = (e) => {
        bytes += e.data.size;
        chunks.push(e.data);
        if (bytes > 24 * 1024 * 1024 && r.state === 'recording') r.stop();
      };
      r.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        if (alive.current) {
          setRecording(false);
          setAudio(new Blob(chunks, { type: r.mimeType.split(';')[0] }));
        }
      };
      recorder.current = r;
      r.start(1000);
      setRecording(true);
      setAudio(null);
      setTime(new Date().toISOString());
      reset();
    } catch {
      acquired?.getTracks().forEach((t) => t.stop());
      if (alive.current) setMessage('Microphone unavailable. Type text or load the encounter transcript.');
    } finally {
      if (alive.current) setBusy(false);
    }
  }
  async function save() {
    const body = retry.current ?? {
      encounter_id: encounterId,
      subject_id: subject,
      captured_at: time,
      transcript: text,
      encounter_transcript_hash: sourceHash,
      proposals: selected.map((i) => proposals[i]),
      reviewed_subject_time_and_source: reviewed,
      request_id: crypto.randomUUID(),
    };
    retry.current = body;
    await run(async () => {
      try {
        await clientApi.post(`${base}/voice-proposals`, body);
        if (!alive.current) return;
        setUncertain(false);
        reset();
        setMessage(
          'Proposals staged as Pending review. Accept, amend or strike them in event history before they enter clinical totals or reports.',
        );
        onSaved?.();
      } catch (e) {
        const status = (e as { response?: { status?: number } }).response?.status;
        if (alive.current) {
          setUncertain(!status || status >= 500);
          if (status && status < 500) retry.current = null;
        }
        throw e;
      }
    });
  }
  if (!encounterId) return null;
  return (
    <section
      className="space-y-3 rounded-lg border p-4"
      aria-label="Voice and transcript charting"
      data-encounter-draft={canCreate ? 'true' : undefined}>
      {/* Mark the entire interaction region before the initial Record click so the parent
          encounter guard locks even when the textarea is empty or audio permission is pending. */}
      <h3 className="font-semibold">Voice, transcript backfill and event notes</h3>
      <p className="text-sm text-muted-foreground">
        Choose one subject and the actual observation time. Check every number against the raw transcript. Proposals
        remain pending until reviewed in event history.
      </p>
      {offline && (
        <p role="status">
          Voice processing requires a connection. Your text and recording are retained in this open panel; reconnect to
          retry. Manual offline charting remains available. Nothing uploads automatically.
        </p>
      )}
      {canCreate && (
        <>
          <div className="flex flex-wrap gap-3">
            <label>
              Subject
              <select
                aria-label="Voice subject"
                className="block rounded border p-2"
                value={subject}
                disabled={locked}
                onChange={(e) => {
                  setSubject(e.target.value);
                  reset();
                }}>
                <option value="">Choose explicitly</option>
                {episode.subjects
                  .filter((s) => s.active !== false && (!allowedSubjectIds || allowedSubjectIds.includes(s.id)))
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label} ({s.subject_type})
                    </option>
                  ))}
              </select>
            </label>
            <label>
              Observation time with time zone
              <input
                aria-label="Voice observation time"
                className="block rounded border p-2"
                value={time}
                disabled={locked}
                onChange={(e) => {
                  setTime(e.target.value);
                  setReviewed(false);
                }}
              />
            </label>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button disabled={busy || uncertain || (offline && !recording)} onClick={() => void record()}>
              {recording ? 'Stop recording' : 'Record dictation'}
            </Button>
            {audio && (
              <Button
                disabled={networkDisabled}
                onClick={() =>
                  void run(async () => {
                    const form = new FormData();
                    form.append('file', audio, 'dictation');
                    const r = await clientApi.post(`${base}/encounters/${encounterId}/voice-transcribe`, form);
                    if (alive.current) {
                      setText(r.data.text);
                      setSourceHash(null);
                      reset();
                    }
                  })
                }>
                Transcribe retained recording
              </Button>
            )}
            <Button
              variant="outline"
              disabled={networkDisabled}
              onClick={() =>
                void run(async () => {
                  const r = await clientApi.get(`${base}/encounters/${encounterId}/voice-source`);
                  if (alive.current) {
                    setText(r.data.transcript);
                    setSourceHash(r.data.source_hash);
                    reset();
                  }
                })
              }>
              Load encounter transcript
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Audio stays in memory until this panel closes. It is sent only when you choose Transcribe. Offline audio
            recovery and ambient recording are not enabled.
          </p>
          <Textarea
            aria-label="Raw dictation transcript"
            rows={5}
            value={text}
            disabled={locked}
            onChange={(e) => {
              setText(e.target.value);
              setSourceHash(null);
              reset();
            }}
          />
          <Button
            disabled={networkDisabled || !subject || !text.trim() || !Number.isFinite(Date.parse(time))}
            onClick={() =>
              void run(async () => {
                const r = await clientApi.post(`${base}/voice-preview`, {
                  encounter_id: encounterId,
                  subject_id: subject,
                  captured_at: time,
                  transcript: text,
                  encounter_transcript_hash: sourceHash,
                });
                if (alive.current) {
                  setProposals(r.data.proposals);
                  setSelected(r.data.proposals.map((_: Proposal, i: number) => i));
                  setReviewed(false);
                  setMessage(
                    `${r.data.proposals.length} proposals; ${r.data.omitted} invalid proposals omitted. Verify numbers, units, subject and spoken times.`,
                  );
                }
              })
            }>
            Extract proposals
          </Button>
          {proposals.map((p, i) => (
            <div key={i} className="rounded border p-3">
              <label className="flex gap-2 font-semibold">
                <input
                  type="checkbox"
                  checked={selected.includes(i)}
                  disabled={locked}
                  onChange={(e) => {
                    setSelected((v) => (e.target.checked ? [...v, i] : v.filter((n) => n !== i)));
                    setReviewed(false);
                  }}
                />
                {p.event_type}
              </label>
              <dl className="grid grid-cols-2 gap-2 text-lg">
                {Object.entries(p.payload).map(([key, value]) => (
                  <div key={key}>
                    <dt className="text-sm text-muted-foreground">{key.replaceAll('_', ' ')}</dt>
                    <dd className="font-semibold">
                      {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                    </dd>
                  </div>
                ))}
              </dl>
              <blockquote className="mt-2 border-l-2 pl-2 text-sm">{p.evidence_span}</blockquote>
            </div>
          ))}
          {!!proposals.length && (
            <>
              <label className="flex gap-2">
                <input
                  type="checkbox"
                  disabled={locked}
                  checked={reviewed}
                  onChange={(e) => setReviewed(e.target.checked)}
                />
                I checked the selected subject, actual observation time, units and source text. I will review each
                pending event before clinical use.
              </label>
              <Button
                disabled={offline || busy || recording || (!uncertain && (!reviewed || !selected.length))}
                onClick={() => void save()}>
                {uncertain ? 'Retry same proposal save' : 'Stage selected for review'}
              </Button>
            </>
          )}
        </>
      )}
      {!!templates.length && (
        <label>
          Note outline template
          <select
            aria-label="Note outline template"
            value={templateId}
            disabled={locked}
            onChange={(e) => {
              setTemplateId(e.target.value);
              setNotes(null);
            }}>
            <option value="">All confirmed events</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.title}
              </option>
            ))}
          </select>
        </label>
      )}
      <Button
        variant="outline"
        disabled={networkDisabled}
        onClick={() =>
          void run(async () => {
            const r = await clientApi.get(`${base}/encounters/${encounterId}/event-note`, {
              params: templateId ? { template_id: templateId } : {},
            });
            if (alive.current) setNotes(r.data.sections);
          })
        }>
        Preview note from confirmed events
      </Button>
      {notes && (
        <div>
          <p className="text-sm">
            Confirmed event source outline. Open Reports to create and sign the complete narrative. Pending observations
            are excluded.
          </p>
          {notes.map((n) => (
            <details key={n.event_id}>
              <summary>
                {n.heading ? `${n.heading} — ` : ''}
                {n.event_type} · {new Date(n.occurred_at).toLocaleString()} ·{' '}
                {n.numeric_verification ? 'Numbers verified' : 'Review needed'}
              </summary>
              <pre className="whitespace-pre-wrap break-words text-sm">{n.text}</pre>
              <p className="text-xs">
                Source event: {n.event_id} · Subject:{' '}
                {episode.subjects.find((s) => s.id === n.subject_id)?.label ?? n.subject_id}
              </p>
            </details>
          ))}
          {!notes.length && <p>No confirmed events in this encounter.</p>}
        </div>
      )}
      {message && <p role="status">{message}</p>}
    </section>
  );
}
